/**
 * EmployeeAddressesViewElement — blok „Adresy pracownika" jako przenośny klocek
 * kreatora layoutu (konfigurowalne zakładki, kartoteka Pracownicy).
 *
 * Mirror `ContractorAddressesViewElement` (ta sama encja `addresses`, polymorphic
 * ownerType). W builderze renderuje się przez wspólny `FormatkaReorderList`: kolumny
 * CORE adresu, każda z uchwytem przeciągania (kolejność → `settings.columnOrder`) i
 * ikonką oka (ukryj → `settings.hiddenColumnIds`). Read-side `AddressesDynamicTab`
 * czyta oba. `fullWidth: true`.
 */

import { MapPin } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

/** Stałe kolumny CORE listy adresów (slug = pole helper-encji `addresses`). */
const ADDRESS_COLUMNS: ReadonlyArray<{ slug: string; labelKey: string; defaultLabel: string }> = [
  {
    slug: 'kind',
    labelKey: 'tenant.employees.addressesBlock.columns.kind',
    defaultLabel: 'Typ adresu',
  },
  {
    slug: 'street',
    labelKey: 'tenant.employees.addressesBlock.columns.street',
    defaultLabel: 'Ulica',
  },
  {
    slug: 'building_no',
    labelKey: 'tenant.employees.addressesBlock.columns.buildingNo',
    defaultLabel: 'Nr budynku',
  },
  {
    slug: 'apartment_no',
    labelKey: 'tenant.employees.addressesBlock.columns.apartmentNo',
    defaultLabel: 'Nr lokalu',
  },
  {
    slug: 'postal_code',
    labelKey: 'tenant.employees.addressesBlock.columns.postalCode',
    defaultLabel: 'Kod pocztowy',
  },
  {
    slug: 'city',
    labelKey: 'tenant.employees.addressesBlock.columns.city',
    defaultLabel: 'Miejscowość',
  },
  {
    slug: 'country_code',
    labelKey: 'tenant.employees.addressesBlock.columns.country',
    defaultLabel: 'Kraj',
  },
  {
    slug: 'region',
    labelKey: 'tenant.employees.addressesBlock.columns.region',
    defaultLabel: 'Województwo',
  },
];

const COLUMN_BY_SLUG = new Map(ADDRESS_COLUMNS.map((c) => [c.slug, c]));

/** Bezpieczny odczyt `settings.hiddenColumnIds` → lista slugów ukrytych kolumn. */
export function parseHiddenEmployeeAddressColumns(
  settings: Record<string, unknown> | undefined,
): string[] {
  const raw = settings?.hiddenColumnIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string');
}

export const EmployeeAddressesViewElement: LayoutElementDefinition = {
  type: 'employee_addresses_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Adresy pracownika',
  icon: 'map-pin',
  allowedModules: ['employees'],
  defaultSettings: () => ({}),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const hidden = new Set(parseHiddenEmployeeAddressColumns(element.settings));
    const orderedSlugs = applyOrder(
      ADDRESS_COLUMNS.map((c) => c.slug),
      parseSlugList(element.settings, 'columnOrder'),
    );
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedSlugs.map((slug) => {
      const col = COLUMN_BY_SLUG.get(slug);
      return {
        slug,
        label: col ? i18n.t(col.labelKey, { defaultValue: col.defaultLabel }) : slug,
        hint: i18n.t('tenant.employees.addressesBlock.columnKind', { defaultValue: 'kolumna' }),
      };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hidden}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ columnOrder: reorderSlugs(orderedSlugs, active, over) })
        }
        onToggleHidden={(slug) => {
          const next = new Set(hidden);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          onSettingsChange?.({ hiddenColumnIds: Array.from(next) });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <MapPin className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.employees.addressesBlock.paletteLabel', {
              defaultValue: 'Adresy pracownika',
            })}
            <span className="text-text-muted">
              {' · '}
              {i18n.t('tenant.employees.addressesBlock.columnsHint', {
                defaultValue: 'kolumny listy',
              })}
            </span>
          </p>
        }
        hiddenLabel={i18n.t('tenant.employees.addressesBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.employees.addressesBlock.hideField', {
            defaultValue: 'Ukryj kolumnę',
          }),
          show: i18n.t('tenant.employees.addressesBlock.showField', {
            defaultValue: 'Pokaż kolumnę',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`employee-addresses-view-element-${element.id}`}
        rowTestIdPrefix="employee-addresses-column"
        elementType="employee_addresses_view"
      />
    );
  },
};
